import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiAlertCircle, FiArrowLeft, FiCheck, FiDownload, FiShield, FiTarget } from 'react-icons/fi';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { api } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Button, Card } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import SafeIcon from '../../common/SafeIcon';
import {
  resolveDisplayName,
  useUsersDirectory,
} from '../../lib/usersDirectory';
import {
  annualizeAmount,
  calculateTotalAnnualIncome,
  calculateTotalAnnualExpenses,
  calculateNetIncome,
  calculateNetWorth,
  calculateFIN,
  calculateFinancialIndependenceNumber,
  calculateInsuranceNeed,
  calculateGoalProgress,
  calculateEstateNeedsAttentionCount,
  calculateEstateNeedsAttentionPct,
  getKeyRecommendations,
} from '../../lib/financialAnalysisUtils';
import { downloadBlob, packHtmlElementsToPdfBlob } from '../../lib/pdfSectionPacker';

const DEFAULT_FINAL_EXPENSES = 25000;

const ESTATE_ITEM_ORDER = [
  'will',
  'powerOfAttorney',
  'healthcareDirective',
  'trust',
  'beneficiaryDesignations',
  'guardianship',
  'emergencyFund',
  'taxPlanning',
];

const ESTATE_ITEM_LABELS = {
  will: 'Will',
  powerOfAttorney: 'Power of Attorney',
  healthcareDirective: 'Healthcare Directive',
  trust: 'Trust',
  beneficiaryDesignations: 'Beneficiary Designations',
  guardianship: 'Guardianship',
  emergencyFund: 'Emergency Fund',
  taxPlanning: 'Tax Planning',
};

const INCOME_CATEGORY_LABELS = {
  primary: 'Primary',
  spouse: 'Spouse',
  business: 'Business',
  real_estate: 'Real Estate',
  investment: 'Investment',
  other: 'Other',
};

/** Solid fills only — gradients/animations can fail under html2canvas PDF capture. */
const CHART_COLORS = [
  '#00AAFF',
  '#1B4C87',
  '#10B981',
  '#F59E0B',
  '#8B5CF6',
  '#EC4899',
  '#14B8A6',
  '#F97316',
  '#6366F1',
  '#84CC16',
];

const ASSETS_COLOR = '#00AAFF';
const LIABILITIES_COLOR = '#94A3B8';
const NEEDS_COLOR = '#1B4C87';
const RESOURCES_COLOR = '#00AAFF';
const GAP_COLOR = '#F59E0B';

function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(val) || 0);
}

function formatCompactCurrency(val) {
  const n = Number(val) || 0;
  if (Math.abs(n) >= 1_000_000) {
    return `$${(n / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(n) >= 1_000) {
    return `$${(n / 1_000).toFixed(0)}K`;
  }
  return formatCurrency(n);
}

function formatDate(val) {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function leadDisplayName(lead) {
  if (!lead) return 'Client';
  const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ').trim();
  return name || 'Client';
}

function resolveFinalExpenses(override) {
  if (override === '' || override == null) return DEFAULT_FINAL_EXPENSES;
  return Number(override);
}

function sanitizeFilePart(value) {
  const cleaned = String(value || '')
    .trim()
    .replace(/[^\w\-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  return cleaned || 'Client';
}

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function humanizeCategory(raw, labelMap = {}) {
  if (!raw) return 'Other';
  if (labelMap[raw]) return labelMap[raw];
  return String(raw)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const EMPLOYMENT_LABELS = {
  employed: 'Employed',
  self_employed: 'Self-Employed',
  unemployed: 'Unemployed',
  retired: 'Retired',
  student: 'Student',
  other: 'Other',
};

const MARITAL_LABELS = {
  single: 'Single',
  married: 'Married',
  divorced: 'Divorced',
  widowed: 'Widowed',
  separated: 'Separated',
  domestic_partner: 'Domestic Partner',
};

const RELATIONSHIP_LABELS = {
  child: 'Child',
  stepchild: 'Stepchild',
  other: 'Other',
};

function InfoCell({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-900 mt-0.5 break-words">{value || '—'}</p>
    </div>
  );
}

function groupAnnualizedByCategory(rows, labelMap = {}) {
  const map = new Map();
  for (const row of rows || []) {
    const key = row.category || 'other';
    map.set(key, (map.get(key) || 0) + annualizeAmount(row.amount, row.frequency));
  }
  return [...map.entries()]
    .map(([category, value]) => ({
      name: humanizeCategory(category, labelMap),
      value: Math.round(value),
      category,
    }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);
}

function SummaryStat({ label, value, emphasize, hint }) {
  return (
    <div
      className={`rounded-lg border px-4 py-3 ${
        emphasize
          ? 'border-primary/30 bg-primary/5'
          : 'border-gray-100 bg-gray-50'
      }`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <p
        className={`text-lg font-semibold tabular-nums mt-1 ${
          emphasize ? 'text-primary' : 'text-gray-900'
        }`}
      >
        {value}
      </p>
      {hint ? <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{hint}</p> : null}
    </div>
  );
}

function ChartEmpty({ message }) {
  return (
    <div className="h-[220px] flex items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/50">
      <p className="text-xs text-gray-400">{message}</p>
    </div>
  );
}

const currencyTooltip = {
  formatter: (value) => formatCurrency(value),
};

export default function FinancialAnalysisReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  useUsersDirectory();

  const [analysis, setAnalysis] = useState(null);
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const sectionRefs = useRef([]);

  const setSectionRef = useCallback((index) => (el) => {
    sectionRefs.current[index] = el;
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.financialAnalysis.getById(id);
      if (!data) {
        addToast({ type: 'error', title: 'Financial analysis not found' });
        navigate('/financial-analysis');
        return;
      }
      setAnalysis(data);

      if (data.agency_id) {
        try {
          const agencyRow = await api.agencies.getById(data.agency_id);
          setAgency(agencyRow);
        } catch {
          setAgency(null);
        }
      }
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Failed to load report',
        message: err?.message,
      });
      navigate('/financial-analysis');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const figures = useMemo(() => {
    if (!analysis) return null;

    const incomeSources = analysis.income_sources || [];
    const expenses = analysis.expenses || [];
    const assets = analysis.assets || [];
    const liabilities = analysis.liabilities || [];
    const insurancePolicies = analysis.insurance_policies || [];
    const financialGoals = analysis.financial_goals || [];
    const estateChecklist = analysis.estate_checklist || [];

    const annualIncome = calculateTotalAnnualIncome(incomeSources);
    const annualExpenses = calculateTotalAnnualExpenses(expenses);
    const netIncome = calculateNetIncome(incomeSources, expenses);
    const netWorth = calculateNetWorth(assets, liabilities);
    const familyIncomeNeed = calculateFIN(incomeSources);
    const financialIndependenceNumber = calculateFinancialIndependenceNumber(expenses);

    const totalAssets = (assets || []).reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    const totalLiabilities = (liabilities || []).reduce(
      (sum, r) => sum + (Number(r.amount) || 0),
      0,
    );

    const insuranceNeed = calculateInsuranceNeed({
      incomeSources,
      assets,
      insurancePolicies,
      yearsToReplace: analysis.years_to_replace || 20,
      finalExpenses: resolveFinalExpenses(analysis.final_expenses_override),
      educationFund: Number(analysis.education_fund) || 0,
    });

    const goals = (financialGoals || []).map((goal) => ({
      ...goal,
      progress: calculateGoalProgress(goal),
    }));

    const estateNeedsAttentionCount = calculateEstateNeedsAttentionCount(estateChecklist);
    const estateNeedsAttentionPct = calculateEstateNeedsAttentionPct(estateChecklist);
    const byKey = new Map((estateChecklist || []).map((item) => [item.item_key, item]));
    const estateItems = ESTATE_ITEM_ORDER.map((key) => ({
      key,
      label: ESTATE_ITEM_LABELS[key] || key,
      needsAttention: Boolean(byKey.get(key)?.needs_attention),
    }));

    const recommendations = getKeyRecommendations({
      netIncome,
      annualIncome,
      insuranceGap: insuranceNeed.coverageGap,
      estateChecklist,
      totalAssets,
      totalLiabilities,
    });

    const expensesByCategory = groupAnnualizedByCategory(expenses);
    const incomeByCategory = groupAnnualizedByCategory(incomeSources, INCOME_CATEGORY_LABELS);

    const balanceSheetChart = [
      { name: 'Assets', value: Math.round(totalAssets) },
      { name: 'Liabilities', value: Math.round(totalLiabilities) },
    ];

    const insuranceCompareChart = [
      {
        name: 'Comparison',
        needs: Math.round(insuranceNeed.totalNeeds),
        resources: Math.round(insuranceNeed.totalResources),
        gap: Math.round(insuranceNeed.coverageGap),
      },
    ];

    return {
      annualIncome,
      annualExpenses,
      netIncome,
      netWorth,
      familyIncomeNeed,
      financialIndependenceNumber,
      totalAssets,
      totalLiabilities,
      insuranceNeed,
      goals,
      estateNeedsAttentionPct,
      estateItems,
      estateNeedsAttentionCount,
      recommendations,
      expensesByCategory,
      incomeByCategory,
      balanceSheetChart,
      insuranceCompareChart,
    };
  }, [analysis]);

  const handleDownloadPdf = async () => {
    if (!analysis || !figures) return;
    setExporting(true);
    try {
      // Let Recharts finish a paint frame before html2canvas (animations are disabled).
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const nodes = sectionRefs.current.filter(Boolean);
      const blob = await packHtmlElementsToPdfBlob(nodes);
      const lastName = sanitizeFilePart(analysis.lead?.last_name || analysis.lead?.first_name);
      const fileName = `FinancialAnalysis_${lastName}_${todayIsoDate()}.pdf`;
      downloadBlob(blob, fileName);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'PDF export failed',
        message: err?.message,
      });
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 gap-3 text-gray-500 text-sm">
        <Spinner className="text-primary" />
        Loading financial analysis report…
      </div>
    );
  }

  if (!analysis || !figures) return null;

  const leadName = leadDisplayName(analysis.lead);
  const agentName =
    resolveDisplayName(analysis.created_by, analysis.creator) || '—';
  const agencyName = agency?.name || 'Agency';
  const reportDate = formatDate(new Date().toISOString());
  const gap = figures.insuranceNeed.coverageGap;
  const insuranceExplanation =
    gap > 0
      ? `Based on your current income, assets, and existing coverage, there is a ${formatCurrency(gap)} gap in your family's financial protection.`
      : 'Based on your current income, assets, and existing coverage, your family\'s financial protection needs appear to be covered.';

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto pb-16">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between print:hidden">
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => navigate(`/financial-analysis/${analysis.id}`)}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
            Back to Editor
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Financial Analysis Report</h1>
            <p className="text-sm text-gray-500 mt-1">{leadName}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/financial-analysis/${analysis.id}`)}
          >
            Back to Editor
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleDownloadPdf}
            disabled={exporting}
            isLoading={exporting}
          >
            <SafeIcon icon={FiDownload} className="w-4 h-4 mr-1.5" />
            Download PDF
          </Button>
        </div>
      </div>

      {/* Report body — each section is captured intact for PDF packing */}
      <div className="space-y-6">
        {/* 1. Header */}
        <div ref={setSectionRef(0)}>
          <Card className="p-6 overflow-visible">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex items-start gap-4 min-w-0 flex-1">
                {agency?.logo_url ? (
                  <img
                    src={agency.logo_url}
                    alt={agencyName}
                    className="h-14 w-14 object-contain rounded-lg border border-gray-100 bg-white flex-shrink-0"
                  />
                ) : (
                  <div className="h-14 w-14 rounded-lg bg-[#1B4C87] text-white flex items-center justify-center text-lg font-bold flex-shrink-0">
                    {(agencyName || 'A').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Financial Needs Analysis
                  </p>
                  <h2 className="text-xl font-bold text-gray-900 break-words whitespace-normal leading-snug">
                    {agencyName}
                  </h2>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 text-sm lg:flex-shrink-0 lg:max-w-md">
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Client</p>
                  <p className="font-medium text-gray-900 mt-0.5 break-words">{leadName}</p>
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Agent</p>
                  <p className="font-medium text-gray-900 mt-0.5 break-words">{agentName}</p>
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Report Date</p>
                  <p className="font-medium text-gray-900 mt-0.5">{reportDate}</p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* 2. Household Summary */}
        <div ref={setSectionRef(1)}>
          <Card className="p-5 space-y-4">
            <h2 className="text-base font-semibold text-gray-900">Household Summary</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <InfoCell
                label="Date of Birth"
                value={analysis.date_of_birth ? formatDate(analysis.date_of_birth) : null}
              />
              <InfoCell
                label="Employment Status"
                value={
                  analysis.employment_status
                    ? EMPLOYMENT_LABELS[analysis.employment_status] ||
                      humanizeCategory(analysis.employment_status)
                    : null
                }
              />
              <InfoCell label="Occupation" value={analysis.occupation} />
              <InfoCell
                label="Marital Status"
                value={
                  analysis.marital_status
                    ? MARITAL_LABELS[analysis.marital_status] ||
                      humanizeCategory(analysis.marital_status)
                    : null
                }
              />
            </div>

            {(analysis.marital_status === 'married' ||
              analysis.marital_status === 'domestic_partner') && (
              <div className="rounded-lg border border-gray-100 bg-gray-50/40 p-4 space-y-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  Spouse / Partner
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <InfoCell
                    label="Name"
                    value={[analysis.spouse_first_name, analysis.spouse_last_name]
                      .filter(Boolean)
                      .join(' ')}
                  />
                  <InfoCell label="Email" value={analysis.spouse_email} />
                  <InfoCell label="Phone" value={analysis.spouse_phone} />
                  <InfoCell
                    label="Date of Birth"
                    value={formatDate(analysis.spouse_date_of_birth)}
                  />
                </div>
              </div>
            )}

            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
                Dependents
              </p>
              {(analysis.dependents || []).length === 0 ? (
                <p className="text-sm text-gray-500">No dependents recorded.</p>
              ) : (
                <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
                  {(analysis.dependents || []).map((dep) => (
                    <li
                      key={dep.id}
                      className="bg-white px-4 py-2.5 flex flex-wrap items-center justify-between gap-2 text-sm"
                    >
                      <span className="font-medium text-gray-900">
                        {dep.full_name?.trim() || 'Unnamed'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {RELATIONSHIP_LABELS[dep.relationship] || humanizeCategory(dep.relationship)}
                        {dep.date_of_birth ? ` · DOB ${formatDate(dep.date_of_birth)}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>
        </div>

        {/* 3. Cashflow */}
        <div ref={setSectionRef(2)}>
          <Card className="p-5 space-y-4">
            <h2 className="text-base font-semibold text-gray-900">Cashflow Summary</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <SummaryStat label="Total Annual Income" value={formatCurrency(figures.annualIncome)} />
              <SummaryStat label="Total Annual Expenses" value={formatCurrency(figures.annualExpenses)} />
              <SummaryStat
                label="Net Income"
                value={formatCurrency(figures.netIncome)}
                emphasize
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
              <div className="rounded-lg border border-gray-100 bg-gray-50/40 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
                  Expenses by Category
                </p>
                {figures.expensesByCategory.length === 0 ? (
                  <ChartEmpty message="No expenses to chart yet." />
                ) : (
                  <div className="h-[220px] w-full">
                    <ResponsiveContainer width="100%" height="100%" debounce={1}>
                      <PieChart>
                        <Pie
                          data={figures.expensesByCategory}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={52}
                          outerRadius={78}
                          paddingAngle={2}
                          isAnimationActive={false}
                        >
                          {figures.expensesByCategory.map((entry, i) => (
                            <Cell
                              key={entry.category || entry.name}
                              fill={CHART_COLORS[i % CHART_COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip {...currencyTooltip} />
                        <Legend
                          verticalAlign="bottom"
                          height={36}
                          wrapperStyle={{ fontSize: 11 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-gray-100 bg-gray-50/40 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
                  Income by Category
                </p>
                {figures.incomeByCategory.length === 0 ? (
                  <ChartEmpty message="No income sources to chart yet." />
                ) : (
                  <div className="h-[220px] w-full">
                    <ResponsiveContainer width="100%" height="100%" debounce={1}>
                      <BarChart
                        data={figures.incomeByCategory}
                        layout="vertical"
                        margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
                        <XAxis
                          type="number"
                          tick={{ fontSize: 11, fill: '#9CA3AF' }}
                          tickFormatter={formatCompactCurrency}
                        />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={88}
                          tick={{ fontSize: 11, fill: '#6B7280' }}
                        />
                        <Tooltip {...currencyTooltip} />
                        <Bar
                          dataKey="value"
                          name="Annual Income"
                          fill={ASSETS_COLOR}
                          radius={[0, 4, 4, 0]}
                          barSize={18}
                          isAnimationActive={false}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* 4. Balance Sheet */}
        <div ref={setSectionRef(3)}>
          <Card className="p-5 space-y-4">
            <h2 className="text-base font-semibold text-gray-900">Balance Sheet Summary</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <SummaryStat label="Total Assets" value={formatCurrency(figures.totalAssets)} />
              <SummaryStat label="Total Liabilities" value={formatCurrency(figures.totalLiabilities)} />
              <SummaryStat
                label="Net Worth"
                value={formatCurrency(figures.netWorth)}
                emphasize
              />
            </div>

            <div className="rounded-lg border border-gray-100 bg-gray-50/40 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
                Assets vs Liabilities
              </p>
              <div className="h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%" debounce={1}>
                  <BarChart
                    data={figures.balanceSheetChart}
                    margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#6B7280' }} />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#9CA3AF' }}
                      tickFormatter={formatCompactCurrency}
                      width={56}
                    />
                    <Tooltip {...currencyTooltip} />
                    <Bar dataKey="value" name="Amount" radius={[4, 4, 0, 0]} barSize={56} isAnimationActive={false}>
                      {figures.balanceSheetChart.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={entry.name === 'Assets' ? ASSETS_COLOR : LIABILITIES_COLOR}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>
        </div>

        {/* 5. Insurance Needs */}
        <div ref={setSectionRef(4)}>
          <Card className="p-5 space-y-4">
            <div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiShield} className="w-5 h-5 text-primary flex-shrink-0" />
                <h2 className="text-base font-semibold text-gray-900">Insurance Needs Analysis</h2>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Family Income Need (20× annual income): {formatCurrency(figures.familyIncomeNeed)}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <SummaryStat
                label="Existing Coverage"
                value={formatCurrency(figures.insuranceNeed.existingCoverage)}
              />
              <SummaryStat
                label="Total Needs"
                value={formatCurrency(figures.insuranceNeed.totalNeeds)}
              />
              <SummaryStat
                label="Total Resources"
                value={formatCurrency(figures.insuranceNeed.totalResources)}
              />
            </div>

            <div className="rounded-lg border border-gray-100 bg-gray-50/40 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
                Needs vs Resources
              </p>
              <div className="h-[220px] w-full">
                <ResponsiveContainer width="100%" height="100%" debounce={1}>
                  <BarChart
                    data={figures.insuranceCompareChart}
                    margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="name" tick={false} axisLine={false} />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#9CA3AF' }}
                      tickFormatter={formatCompactCurrency}
                      width={56}
                    />
                    <Tooltip {...currencyTooltip} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar
                      dataKey="needs"
                      name="Total Needs"
                      fill={NEEDS_COLOR}
                      radius={[4, 4, 0, 0]}
                      barSize={44}
                      isAnimationActive={false}
                    />
                    <Bar
                      dataKey="resources"
                      name="Total Resources"
                      fill={RESOURCES_COLOR}
                      radius={[4, 4, 0, 0]}
                      barSize={44}
                      isAnimationActive={false}
                    />
                    <Bar
                      dataKey="gap"
                      name="Coverage Gap"
                      fill={GAP_COLOR}
                      radius={[4, 4, 0, 0]}
                      barSize={44}
                      isAnimationActive={false}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-xl border-2 border-primary/30 bg-primary/5 px-5 py-6 text-center">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">
                Coverage Gap
              </p>
              <p className="text-3xl font-bold text-gray-900 tabular-nums">
                {formatCurrency(figures.insuranceNeed.coverageGap)}
              </p>
              <p className="text-sm text-gray-600 mt-3 max-w-xl mx-auto leading-relaxed">
                {insuranceExplanation}
              </p>
            </div>
          </Card>
        </div>

        {/* 6. Goals */}
        <div ref={setSectionRef(5)}>
          <Card className="p-5 space-y-4">
            <h2 className="text-base font-semibold text-gray-900">Financial Goals</h2>
            {figures.goals.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No financial goals recorded.</p>
            ) : (
              <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
                {figures.goals.map((goal) => {
                  const pct = Math.round(goal.progress?.progressPct || 0);
                  return (
                    <li key={goal.id} className="bg-white px-4 py-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <SafeIcon
                              icon={FiTarget}
                              className="w-4 h-4 text-primary flex-shrink-0"
                            />
                            <p className="text-sm font-medium text-gray-900">
                              {goal.title || 'Untitled goal'}
                            </p>
                          </div>
                          {goal.description ? (
                            <p className="text-xs text-gray-500 mt-0.5 pl-6 leading-relaxed">
                              {goal.description}
                            </p>
                          ) : null}
                          <p className="text-xs text-gray-500 mt-0.5 pl-6">
                            Target {formatCurrency(goal.target_amount)}
                            {goal.target_date ? ` · ${formatDate(goal.target_date)}` : ''}
                          </p>
                        </div>
                        <span className="text-sm font-semibold tabular-nums text-primary">{pct}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden mt-2">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>

        {/* 7. Financial Independence Number */}
        <div ref={setSectionRef(6)}>
          <Card className="p-5 space-y-4">
            <h2 className="text-base font-semibold text-gray-900">
              Your Financial Independence Number
            </h2>
            <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-4">
              <p className="text-2xl font-semibold tabular-nums text-primary">
                {formatCurrency(figures.financialIndependenceNumber)}
              </p>
              <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                This is the amount needed to be financially independent, based on your current annual expenses.
              </p>
            </div>
          </Card>
        </div>

        {/* 8. Estate Planning */}
        <div ref={setSectionRef(7)}>
          <Card className="p-5 space-y-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Estate Planning</h2>
              <p className="text-xs text-gray-500 mt-1">
                {figures.estateNeedsAttentionCount} of 8 need attention
              </p>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden mt-2">
                <div
                  className="h-full rounded-full bg-amber-500 transition-all"
                  style={{
                    width: `${Math.min(100, Math.max(0, figures.estateNeedsAttentionPct))}%`,
                  }}
                />
              </div>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {figures.estateItems.map((item) => (
                <li
                  key={item.key}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                    item.needsAttention
                      ? 'border-amber-200 bg-amber-50/70 text-amber-950'
                      : 'border-gray-100 bg-gray-50/60 text-gray-600'
                  }`}
                >
                  <SafeIcon
                    icon={item.needsAttention ? FiAlertCircle : FiCheck}
                    className={`w-4 h-4 flex-shrink-0 ${
                      item.needsAttention ? 'text-amber-600' : 'text-emerald-600'
                    }`}
                  />
                  <span>{item.label}</span>
                  <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    {item.needsAttention ? 'Needs Attention' : 'No Action Needed'}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        {/* 9. Key Recommendations */}
        <div ref={setSectionRef(8)}>
          <Card className="p-5 border-2 border-amber-200 bg-amber-50/60 space-y-3">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-amber-600" />
              <h2 className="text-base font-semibold text-amber-900">Key Recommendations</h2>
            </div>
            <p className="text-xs text-amber-800/80">
              Actionable guidance based on cashflow, balance sheet, insurance need, and estate checklist.
            </p>
            <ul className="space-y-2">
              {figures.recommendations.map((text, idx) => (
                <li
                  key={idx}
                  className="rounded-lg bg-white/80 border border-amber-100 px-3 py-2.5 text-sm text-gray-800"
                >
                  {text}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
